import { formatAmount } from './format';
import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

export type FinanceImportStatus =
  | 'pending'
  | 'parsing'
  | 'ready'
  | 'committed'
  | 'failed'
  | 'cancelled'
  | 'rolled_back';

export type FinanceImportBatchRow = {
  rowIndex: number;
  date: string | null;
  amount: number | null;
  direction: 'expense' | 'income' | 'transfer' | null;
  currencyCode: string | null;
  categoryLabel: string | null;
  categoryId: number | null;
  accountHint: string | null;
  accountId: number | null;
  toAccountId: number | null;
  notes: string | null;
  selected: boolean;
  duplicateOfTransactionId: number | null;
  duplicateMatch: 'exact' | 'fuzzy' | null;
  /** The existing ledger row this import row matched, when the server could load it. */
  duplicateOf?: {
    date: string;
    amount: number;
    currencyCode: string;
    notes: string | null;
  } | null;
  warnings: string[];
  errors: string[];
};

export type FinanceImportBatch = {
  id: string;
  status: FinanceImportStatus;
  sourceKind: 'file' | 'paste';
  filename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  model: string | null;
  usage: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    cost?: number;
  } | null;
  stats: {
    totalRows?: number;
    validRows?: number;
    duplicateRows?: number;
    importedRows?: number;
    skippedRows?: number;
    linkedRows?: number;
    editedSinceImport?: number;
    progress?: {
      phase: string;
      detail: string;
      at?: string;
    };
    sheetNote?: string;
  };
  error: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  committedAt: string | null;
  rolledBackAt: string | null;
  sourceLabel: string;
  pastePreview: string | null;
  rows?: FinanceImportBatchRow[];
  rejectedRows?: Array<{ row: number; reason: string; raw: string }>;
  extractedTextPreview?: string | null;
};

function formatDuplicateDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Review caption for a row that matches an existing transaction. */
export function duplicateNote(
  row: Pick<FinanceImportBatchRow, 'duplicateMatch' | 'duplicateOf'>,
): string {
  const label = row.duplicateMatch === 'exact' ? 'Duplicate' : 'Likely duplicate';
  const target = row.duplicateOf;
  if (!target) return label;
  const amount = `${formatAmount(Math.abs(target.amount))} ${target.currencyCode}`;
  const details = target.notes?.trim();
  const summary = `${formatDuplicateDate(target.date)}, ${amount}`;
  return details ? `${label} of ${summary}, ${details}` : `${label} of ${summary}`;
}

/**
 * Amount stored from the preview direction.
 * A credit is stored negative, including a transfer into the statement account,
 * so the ledger can label the other account From. A debit or an unspecified
 * transfer is stored positive.
 */
export function signedImportCommitAmount(
  amount: number,
  direction: 'expense' | 'income' | 'transfer' | null,
  categoryKind: 'expense' | 'income' | 'transfer' | null | undefined,
): number {
  if (direction == null) return amount;
  const abs = Math.abs(amount);
  if (categoryKind === 'transfer' && direction !== 'income') return abs;
  return direction === 'income' ? -abs : abs;
}

/** Keep the statement account in the account field. A transfer into it is negative. */
export function transferCommitLegs(input: {
  statementAccountId: number | null;
  amount: number;
  fromAccountId: number;
  toAccountId: number;
  linkedTransactionId: number | null;
}): { accountId: number; toAccountId: number; amount: number } {
  const abs = Math.abs(input.amount);
  if (input.linkedTransactionId != null) {
    return { accountId: input.fromAccountId, toAccountId: input.toAccountId, amount: abs };
  }
  const statementId = input.statementAccountId;
  const anchored =
    statementId != null &&
    (input.fromAccountId === statementId || input.toAccountId === statementId);
  if (!anchored || statementId == null) {
    return { accountId: input.fromAccountId, toAccountId: input.toAccountId, amount: abs };
  }
  const inbound = input.toAccountId === statementId;
  return {
    accountId: statementId,
    toAccountId: inbound ? input.fromAccountId : input.toAccountId,
    amount: inbound ? -abs : abs,
  };
}

export type CommitImportRowInput = {
  rowIndex: number;
  selected: boolean;
  date: string;
  amount: number;
  currencyCode: string;
  accountId?: number | null;
  toAccountId?: number | null;
  categoryId?: number | null;
  notes?: string | null;
  linkedTransactionId?: number | null;
};

async function authHeaders(json = true): Promise<HeadersInit> {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...getDevAuthHeaders(),
    ...(await getNativeBearerAuthHeaders()),
  };
}

function parseErrorMessage(text: string, status: number): string {
  try {
    const body = JSON.parse(text) as { error?: { message?: string } };
    if (body.error?.message) return body.error.message;
  } catch {
    // fall through
  }
  return text || `Request failed (${status})`;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
  return res.json() as Promise<T>;
}

export async function createSmartImportFromPaste(text: string): Promise<FinanceImportBatch> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(true),
    body: JSON.stringify({ text }),
  });
  return (await parseJson<{ batch: FinanceImportBatch }>(res)).batch;
}

export async function createSmartImportFromFile(file: File): Promise<FinanceImportBatch> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${base}/rpc/finance/imports`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(false),
    body: form,
  });
  return (await parseJson<{ batch: FinanceImportBatch }>(res)).batch;
}

export async function fetchImportBatches(): Promise<FinanceImportBatch[]> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports`, {
    credentials: 'include',
    headers: await authHeaders(true),
  });
  return (await parseJson<{ batches: FinanceImportBatch[] }>(res)).batches;
}

export async function fetchImportBatch(id: string): Promise<FinanceImportBatch> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}`, {
    credentials: 'include',
    headers: await authHeaders(true),
  });
  return (await parseJson<{ batch: FinanceImportBatch }>(res)).batch;
}

export async function commitImportBatch(
  id: string,
  rows: CommitImportRowInput[],
): Promise<{ imported: number; skipped: number; linked: number; batch: FinanceImportBatch }> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}/commit`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(true),
    body: JSON.stringify({ rows }),
  });
  return parseJson(res);
}

export async function rollbackImportBatch(
  id: string,
): Promise<{ deleted: number; editedSinceImport: number; batch: FinanceImportBatch }> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}/rollback`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(true),
  });
  return parseJson(res);
}

export async function reparseImportBatch(id: string): Promise<FinanceImportBatch> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}/reparse`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(true),
  });
  return (await parseJson<{ batch: FinanceImportBatch }>(res)).batch;
}

export async function deleteImportBatch(id: string): Promise<void> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: await authHeaders(false),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
}

export async function cancelImportBatch(id: string): Promise<FinanceImportBatch> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const res = await fetch(`${base}/rpc/finance/imports/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(true),
  });
  return (await parseJson<{ batch: FinanceImportBatch }>(res)).batch;
}

export type MerchantCategoryPick = {
  direction: FinanceImportBatchRow['direction'];
  patch: Partial<FinanceImportBatchRow>;
};

/** Trimmed notes, or null when the row has no description. Comparison is exact. */
export function exactDescriptionKey(notes: string | null | undefined): string | null {
  const text = notes?.trim();
  return text ? text : null;
}

/** Other preview rows whose description and direction are exactly the same. */
export function sameDescriptionRowIndexes(
  rows: ReadonlyArray<Pick<FinanceImportBatchRow, 'rowIndex' | 'notes' | 'direction'>>,
  sourceRowIndex: number,
): number[] {
  const source = rows.find((row) => row.rowIndex === sourceRowIndex);
  if (!source) return [sourceRowIndex];
  const key = exactDescriptionKey(source.notes);
  if (!key) return [sourceRowIndex];
  return rows
    .filter(
      (row) =>
        row.rowIndex === sourceRowIndex ||
        (row.direction === source.direction && exactDescriptionKey(row.notes) === key),
    )
    .map((row) => row.rowIndex);
}

/** Rows still arriving take the category the user already chose for those notes. */
export function applyMerchantCategoryPicks(
  rows: FinanceImportBatchRow[],
  picks: Map<string, MerchantCategoryPick>,
  dirty: Set<number>,
): FinanceImportBatchRow[] {
  if (picks.size === 0) return rows;
  return rows.map((row) => {
    if (dirty.has(row.rowIndex)) return row;
    const key = exactDescriptionKey(row.notes);
    const pick = key ? picks.get(key) : undefined;
    if (!pick || pick.direction !== row.direction) return row;
    dirty.add(row.rowIndex);
    return { ...row, ...pick.patch };
  });
}

export function groupRowsByDay(
  rows: FinanceImportBatchRow[],
): Map<string, FinanceImportBatchRow[]> {
  const sorted = [...rows].sort((a, b) => {
    const da = a.date ?? '';
    const db = b.date ?? '';
    if (da !== db) return da.localeCompare(db);
    return a.rowIndex - b.rowIndex;
  });
  const map = new Map<string, FinanceImportBatchRow[]>();
  for (const row of sorted) {
    const day = row.date ?? 'Unknown date';
    const bucket = map.get(day) ?? [];
    bucket.push(row);
    map.set(day, bucket);
  }
  return map;
}

export function detectDefaultAccountId(rows: FinanceImportBatchRow[]): number | null {
  const counts = new Map<number, number>();
  for (const row of rows) {
    if (row.accountId == null) continue;
    counts.set(row.accountId, (counts.get(row.accountId) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [id, count] of counts) {
    if (count > bestCount) {
      best = id;
      bestCount = count;
    }
  }
  return best;
}

/** One account for the whole import: hinted majority, else the sole/currency-matching account. */
export function pickImportAccount(
  accounts: Array<{ id: number; currencyCode: string; isActive: boolean }>,
  rows: FinanceImportBatchRow[],
): number | null {
  const hinted = detectDefaultAccountId(rows);
  if (hinted != null) return hinted;
  const active = accounts.filter((account) => account.isActive);
  if (active.length === 1) return active[0]!.id;
  const codes = rows.map((row) => row.currencyCode).filter((code): code is string => Boolean(code));
  if (codes.length === 0) return null;
  const counts = new Map<string, number>();
  for (const code of codes) counts.set(code, (counts.get(code) ?? 0) + 1);
  let majority = codes[0]!;
  let best = 0;
  for (const [code, count] of counts) {
    if (count > best) {
      majority = code;
      best = count;
    }
  }
  const matching = active.filter((account) => account.currencyCode === majority);
  if (matching.length === 1) return matching[0]!.id;
  return null;
}
